import { adoptStoredAuth, useOAuthStore } from '@tinct/core-store-oauth';
import { getNewToken } from '../oauth-client/oauth-client';

export class TokenManager {
  private refreshPromise: Promise<void> | null = null;
  private refreshLock: string | null = null;
  private readonly LOCK_KEY = 'api-refresh-lock';
  private readonly LOCK_TIMEOUT = 10000;

  constructor() {
    this.setupCrossTabLock();
  }

  private setupCrossTabLock() {
    if (typeof window === 'undefined') return;

    window.addEventListener('storage', (event) => {
      if (event.key === this.LOCK_KEY && event.newValue === null) {
        this.refreshLock = null;
      }
    });
  }

  private acquireLock(): boolean {
    if (typeof window === 'undefined') return true;

    const now = Date.now();
    const currentLock = localStorage.getItem(this.LOCK_KEY);

    if (currentLock) {
      const lockTime = parseInt(currentLock, 10);
      if (now - lockTime < this.LOCK_TIMEOUT) {
        return false;
      }
    }

    const lockId = `${now}-${Math.random()}`;
    localStorage.setItem(this.LOCK_KEY, now.toString());
    this.refreshLock = lockId;

    const storedLock = localStorage.getItem(this.LOCK_KEY);
    return storedLock === now.toString();
  }

  private releaseLock() {
    if (typeof window === 'undefined') return;
    if (this.refreshLock) {
      localStorage.removeItem(this.LOCK_KEY);
      this.refreshLock = null;
    }
  }

  isTokenValid(): boolean {
    const { accessTokenExpirationDate } = useOAuthStore.getState();
    if (!accessTokenExpirationDate) return true;
    return Date.now() < accessTokenExpirationDate - 60000;
  }

  /**
   * Renews the tokens with the refresh token, one tab at a time (the `localStorage` lock).
   *
   * Refresh tokens rotate: each one works once. So whatever renews must spend the refresh token the
   * shared cookie holds NOW, not the copy in this tab's store: the tab that renewed before us
   * releases the lock as soon as it wrote the cookie, while the `storage` event that refreshes our
   * copy is a task of its own that may run after we took the lock. Spending the stale copy got an
   * `invalid_grant`, whose reset emptied the cookie and signed every tab out (token-renewal book,
   * TR-04). Hence `adoptStoredAuth()` once the lock is ours, and again before giving up.
   *
   * @param refusedToken an access token the API just refused (401): renew even though its stored
   *   expiry says it is still valid — the server's clock disagrees (token-renewal book, TR-06) —
   *   unless another tab has already replaced it.
   */
  async refreshToken(refusedToken?: string): Promise<void> {
    if (!this.acquireLock()) {
      await new Promise((resolve) => setTimeout(resolve, 100));
      adoptStoredAuth();
      if (this.hasUsableToken(refusedToken)) {
        return;
      }
      return this.refreshToken(refusedToken);
    }

    try {
      adoptStoredAuth();
      if (this.hasUsableToken(refusedToken)) {
        return;
      }
      const { refreshToken } = useOAuthStore.getState();
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }
      try {
        const response = await getNewToken(refreshToken);
        useOAuthStore.getState().setAuth({
          accessToken: response.accessToken,
          refreshToken: response.refreshToken,
          accessTokenExpirationDate: response.expiresAt,
        });
      } catch (error) {
        // Spent meanwhile by another tab despite the lock (it expired, or a tab without it): its
        // rotation is in the cookie, and resetting would sign every tab out
        adoptStoredAuth();
        if (useOAuthStore.getState().refreshToken !== refreshToken && this.hasUsableToken(refusedToken)) {
          return;
        }
        throw error;
      }
    } finally {
      this.releaseLock();
    }
  }

  /** An access token that is not due, and is not the one the API refused. */
  private hasUsableToken(refusedToken?: string): boolean {
    const { accessToken } = useOAuthStore.getState();
    return !!accessToken && accessToken !== refusedToken && this.isTokenValid();
  }

  async getValidToken(): Promise<string> {
    const { accessToken } = useOAuthStore.getState();

    if (!accessToken) {
      throw new Error('No access token available');
    }

    if (this.isTokenValid()) {
      return accessToken;
    }

    return this.renew();
  }

  /**
   * A token to retry a request the API refused with a 401. The refused token is renewed even when
   * its stored expiry says it is valid: the server judged it expired (clocks disagree), or it was
   * revoked (B-275), in which case the renewal is refused as well and the tokens are cleared.
   */
  async getTokenAfterUnauthorized(refusedToken: string): Promise<string> {
    const { accessToken } = useOAuthStore.getState();
    if (!accessToken) {
      // A request sent without a token (not signed in yet, or signing in right now): nothing to
      // renew, and a reset here would write an empty cookie over the sign-in under way
      throw new Error('No access token available');
    }
    if (accessToken !== refusedToken && this.isTokenValid()) {
      // Renewed meanwhile, by a concurrent request or another tab
      return accessToken;
    }
    return this.renew(refusedToken);
  }

  /** One renewal at a time in this tab; a failed one clears the tokens. */
  private async renew(refusedToken?: string): Promise<string> {
    if (!this.refreshPromise) {
      this.refreshPromise = this.refreshToken(refusedToken).catch(() => {
        useOAuthStore.getState().reset();
      }).finally(() => {
        this.refreshPromise = null;
      });
    }

    await this.refreshPromise;

    const newToken = useOAuthStore.getState().accessToken;
    if (!newToken) {
      throw new Error('Failed to obtain valid token');
    }

    return newToken;
  }

  clearToken() {
    console.log('Clearing token token after repeated 401 responses');
    useOAuthStore.getState().reset();
  }
}
